/**
 * Fleet Control chat staleness verification.
 *
 * Pure decision function. Given a `ChatState` and `nowMs`, classify how
 * confident we are that what the operator sees on screen reflects the
 * latest server-side state. No DOM, no Lit — the result drives a small
 * pill in the chat header and is also logged when the state is bad.
 *
 * `CHAT_STALE_ACTIVE_MS` (5s) guards an in-flight run: deltas should be
 * landing every ~1s while the model streams, so silence >5s is the floor
 * for an honest warning. `CHAT_STALE_IDLE_MS` (30s) lines up with the
 * existing `CHAT_POLL_INTERVAL_MS = 5000` cadence — six missed quiet
 * polls in a row before the pill flips.
 *
 * "Syncing" is the in-between bucket: between a successful refresh and
 * either `fresh` (event just landed) or `stale` (threshold crossed). It
 * exists so the operator can see movement without the pill flickering.
 */

import type { ChatState } from "../controllers/chat.ts";

export const CHAT_STALE_ACTIVE_MS = 5_000;
export const CHAT_STALE_IDLE_MS = 30_000;
/** Within this window of an event, never flag stale. Prevents pill flicker on every delta. */
export const CHAT_FRESH_EVENT_WINDOW_MS = 1_000;
/** Within this window of a successful sync, prefer "fresh" over "syncing". */
export const CHAT_FRESH_SYNC_WINDOW_MS = 3_000;

export type ChatStaleness =
  | { kind: "fresh" }
  | { kind: "syncing"; ageMs: number }
  | {
      kind: "stale";
      reason: "no-recent-event" | "no-recent-sync" | "disconnected";
      ageMs: number;
    };

export function isChatStale(
  state: Pick<ChatState, "chatSync" | "chatRunId" | "chatSending" | "chatStreamStartedAt" | "connected">,
  nowMs: number,
  thresholds?: { activeMs?: number; idleMs?: number },
): ChatStaleness {
  const activeMs = thresholds?.activeMs ?? CHAT_STALE_ACTIVE_MS;
  const idleMs = thresholds?.idleMs ?? CHAT_STALE_IDLE_MS;

  // 1. Disconnected is the loudest signal — flag immediately and skip
  //    the synced/event age math, which would lie while the WS is down.
  if (!state.connected) {
    const lastWsCloseAt = state.chatSync?.lastWsCloseAt;
    const ageMs = typeof lastWsCloseAt === "number" ? Math.max(0, nowMs - lastWsCloseAt) : 0;
    return { kind: "stale", reason: "disconnected", ageMs };
  }

  const sync = state.chatSync ?? {
    lastSyncedAt: null,
    lastEventAt: null,
    lastWsCloseAt: null,
  };

  // 2. Active-run stale — silence while a run is in flight. Anchor to
  //    `chatStreamStartedAt` when no event has landed yet (e.g. the run
  //    just started; first delta can take a couple of seconds).
  const runInFlight = Boolean(state.chatRunId) || state.chatSending === true;
  if (runInFlight) {
    const lastEventAt = sync.lastEventAt ?? state.chatStreamStartedAt;
    if (typeof lastEventAt !== "number") {
      // No anchor at all means the run just started within the active
      // window — treat as fresh so we don't flag the first 5s.
      return { kind: "fresh" };
    }
    const eventAge = nowMs - lastEventAt;
    if (eventAge <= activeMs) {
      return { kind: "fresh" };
    }
    return { kind: "stale", reason: "no-recent-event", ageMs: eventAge };
  }

  // 3. Idle stale — we have not refreshed recently and no run is in flight.
  if (typeof sync.lastSyncedAt === "number") {
    const syncAge = nowMs - sync.lastSyncedAt;
    if (syncAge > idleMs) {
      return { kind: "stale", reason: "no-recent-sync", ageMs: syncAge };
    }
    if (syncAge <= CHAT_FRESH_SYNC_WINDOW_MS) {
      return { kind: "fresh" };
    }
  }

  // 4. Recent event still in flight? Even without a sync, an event < 1s ago means
  //    the transcript is up to date — `fresh`. A few-seconds-old event is
  //    just background activity ("syncing").
  if (typeof sync.lastEventAt === "number") {
    const eventAge = nowMs - sync.lastEventAt;
    if (eventAge <= CHAT_FRESH_EVENT_WINDOW_MS) {
      return { kind: "fresh" };
    }
    return { kind: "syncing", ageMs: eventAge };
  }

  // 5. Sync landed between 3s and 30s ago, no events yet — "syncing"
  //    with the sync age. (No-prior-data path was checked at the top.)
  if (typeof sync.lastSyncedAt === "number") {
    return { kind: "syncing", ageMs: nowMs - sync.lastSyncedAt };
  }

  // 6. Cold start (no sync, no event, not connected, not running).
  //    Treat as fresh so the pill doesn't flash on first paint; the
  //    first loadChatHistory or chat event will stamp the clock anyway.
  return { kind: "fresh" };
}
